/**
 * <ProcessBar> — where a flow instance IS, as a control (§4.3 of the
 * work-surface RFC, Slice 3 of #1000's build plan).
 *
 * ── why this had to move ────────────────────────────────────────────────────
 * There was already a hand-built D365-style BPF in the product: the AFD gate
 * ladder — the double-diamond phase rail in
 * `apps/console/src/app/routes/DeliveryCockpit.tsx` (nodes, "You are here",
 * "Phase n of m", the gate diamonds from `gateLook()`, the governed Advance
 * action, and the UX-08 narrow-viewport work). The RFC's instruction is
 * explicit: *"Do not build a second stage bar beside it"* — extract THAT rail
 * into `@solum/ui` and make the cockpit a consumer, the same
 * `RecordBody → RecordShell` move #964 made. So this file is a PORT of that
 * markup and stylesheet, generalised only in vocabulary (stage for phase,
 * `upcoming` for `pending`, transition for gate), not a new design.
 *
 * ── not `<Steps>` ───────────────────────────────────────────────────────────
 * `controls/Steps.tsx` looks adjacent and is a different thing: a WIZARD's
 * chrome — nodes are buttons, the user jumps back to a reached step, the parent
 * owns `current`. A process bar renders a status-machine/flow instance's
 * POSITION: stages are facts, not navigation; transitions carry a decision's
 * tone (the gate diamonds); moving forward is a governed verb (`advance`), not
 * a click on a node. Collapsing the two would put a "jump anywhere" affordance
 * on a rail whose whole point is that advancing is gated.
 *
 * ── data-driven, no data dependency ─────────────────────────────────────────
 * Nothing here imports `@solum/sdk`, knows an entity key, or fetches anything —
 * the invariant `RecordShell.tsx` and `grid/DataGrid.tsx` state about
 * themselves. The host resolves a flow instance (AFD phases + gates, a
 * `flow_instance`, an onboarding) into plain stage descriptions; this renders
 * them. That is what lets `apps/portal` mount the same bar on a customer's
 * delivery without a table read the bridges withheld.
 *
 * ── the affordance-is-a-prop rule, inside the bar ───────────────────────────
 * The *Advance* action renders only where the host passes `advance` — same rule
 * as `RecordShell.onCommand` and `<DataGrid>`'s selection column. A customer in
 * the portal sees the rail and the position; the governed verb is a prop their
 * surface does not pass. No `advance` ⇒ no button in the accessibility tree at
 * all — not a disabled one.
 */
import { Fragment, useEffect, useRef, type ReactNode } from 'react'
import { cx } from '../controls/cx'
import type { Tone } from '../controls/Pill'
import { Button } from '../controls/Button'
import { readableError } from '@solum/format'
import s from './ProcessBar.module.css'

export type ProcessStageState = 'done' | 'active' | 'upcoming'

/** The decorator rendered on the connector AFTER a stage (AFD: the exit gate). */
export interface ProcessTransition {
  /** Decision tone — drives the diamond's colour (neutral = not yet reached). */
  tone: Tone
  /** The glyph inside the diamond (AFD: ✓, ↺, or the gate number). */
  glyph?: ReactNode
  /** An in-flight transition — pulses (respects prefers-reduced-motion). */
  live?: boolean
  /** Tooltip, e.g. "Gate 4". */
  title?: string
}

export interface ProcessStage {
  key: string
  label: ReactNode
  /** Small caption under the label (AFD: the phase's thinking mode). */
  hint?: ReactNode
  state: ProcessStageState
  /**
   * The glyph inside the node while not done (done always shows ✓). Defaults to
   * the 1-based position; AFD passes the phase's own sort_order.
   */
  marker?: ReactNode
  /**
   * The transition decorator between this stage and the next. Ignored on the
   * last stage — a rail has n−1 connectors.
   */
  transition?: ProcessTransition
}

/** The governed forward verb — a prop precisely so a host can withhold it. */
export interface ProcessAdvance {
  label: ReactNode
  /** The explanation line beside the button ("Gate 4 holds the flow — …"). */
  note?: ReactNode
  onAdvance: () => void
  disabled?: boolean
  loading?: boolean
  /** A refused advance — rendered as a `role="alert"` line under the actions. */
  error?: string
  testId?: string
  noteTestId?: string
  errorTestId?: string
}

export interface ProcessBarProps {
  stages: ProcessStage[]
  /**
   * The bar's REAL accessible name — it is a focusable scrollable region
   * (WCAG 2.1.1), so the name must say what the region is and where the flow
   * stands: "Delivery flow — phase 2 of 7".
   */
  label: string
  /**
   * The compact position line ("Phase 2 of 7 — Build"). Shown only on narrow
   * viewports, where the rail scrolls and the position would otherwise require
   * a swipe to discover (UX-08).
   */
  positionText?: ReactNode
  positionTestId?: string
  /** The marker under the active stage. Empty string suppresses it. */
  hereLabel?: string
  advance?: ProcessAdvance
  className?: string
  testId?: string
}

export function ProcessBar({
  stages, label, positionText, positionTestId, hereLabel = 'You are here',
  advance, className, testId,
}: ProcessBarProps) {
  // Bring the active stage into view (UX-08). Keyed on the active stage's KEY,
  // not the array identity: hosts rebuild the stages array every render, and
  // re-scrolling a rail the user is reading because an unrelated piece of state
  // changed is motion nobody asked for. `block: 'nearest'` so only the rail
  // scrolls, not the page; smooth scrolling is skipped under
  // prefers-reduced-motion, which is motion the user explicitly declined.
  const railRef = useRef<HTMLElement>(null)
  const activeKey = stages.find((st) => st.state === 'active')?.key
  useEffect(() => {
    if (activeKey == null) return
    const el = railRef.current?.querySelector<HTMLElement>('[data-stage-state="active"]')
    if (!el) return
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: smooth ? 'smooth' : 'auto' })
  }, [activeKey])

  // The progress track ends at the active stage. Clamped: with no active stage
  // (a finished or not-yet-started instance) findIndex is -1, and a negative
  // width is an invalid declaration that happened to render as zero — say zero.
  const activeIdx = stages.findIndex((st) => st.state === 'active')
  const progress = stages.length > 1 ? (Math.max(0, activeIdx) / (stages.length - 1)) * 100 : 0

  return (
    /* The query container for the scroll affordances at the foot of
       `ProcessBar.module.css` — the rules it governs style `.railWrap`, and an
       element is never its own query container. `className` deliberately
       stays on `.railWrap`, which is the element a caller has always been
       styling; no current call site passes one. */
    /* ONLY THE RAIL SCROLLS (#2406 rogue census, control-clipped). The slab —
       its ground, its corners, its inset — is this box now, and the scroller
       inside it holds the stages and nothing else. The position line and the
       advance row used to live INSIDE the scroller, so on a phone, once the
       rail scrolled to the active stage, "Advance phase" scrolled with it and
       sat half off the left edge of the slab, and the "where you are" line
       scrolled out of view — the one line whose job is to need no scrolling. */
    <div className={s.railBox}>
    {positionText != null && (
      <p className={s.railPosition} data-testid={positionTestId}>{positionText}</p>
    )}
    <section
      ref={railRef}
      className={cx(s.railWrap, className)}
      aria-label={label}
      tabIndex={0}
      role="group"
      data-testid={testId}
    >
      <div className={s.rail} style={{ ['--progress' as string]: `${progress}%` }}>
        {stages.map((st, i) => (
          <Fragment key={st.key}>
            {/* The current stage says so to assistive technology as well as
                with its ring (#2406 batch-3 visual review): `aria-current="step"`
                is the ARIA for "the step of a process the person is on". */}
            <div className={cx(s.stage, s[st.state])} data-stage-state={st.state}
              aria-current={st.state === 'active' ? 'step' : undefined}>
              <div className={s.node}>{st.state === 'done' ? '✓' : st.marker ?? i + 1}</div>
              <div className={s.stageName}>{st.label}</div>
              {st.hint != null && <div className={s.stageHint}>{st.hint}</div>}
              {st.state === 'active' && hereLabel !== '' && <div className={s.here}>{hereLabel}</div>}
            </div>
            {i < stages.length - 1 && st.transition && (
              <div
                className={cx(s.gate, s[st.transition.tone], st.transition.live && s.gateLive)}
                title={st.transition.title}
              >
                <span className={s.gateGlyph}>{st.transition.glyph}</span>
              </div>
            )}
          </Fragment>
        ))}
      </div>
    </section>
      {advance && (
        <div className={s.railActions}>
          <div className={s.railActionNote} data-testid={advance.noteTestId}>{advance.note}</div>
          <Button
            variant="primary"
            size="sm"
            data-testid={advance.testId}
            disabled={advance.disabled}
            loading={advance.loading}
            onClick={advance.onAdvance}
          >
            {advance.label}
          </Button>
        </div>
      )}
      {advance?.error != null && (
        <p className={s.actionErr} role="alert" data-testid={advance.errorTestId}>{readableError(advance.error)}</p>
      )}
    </div>
  )
}
